import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Pill, Calendar, User, Edit, CheckCircle, XCircle, Loader2, Save } from 'lucide-react';
import { API_URL, ROUTES, STATUS_COLORS, STATUS_LABELS } from './constants';

export const MedicationScheduleTab = () => {
  const [schedule, setSchedule] = useState([]);
  const [patients, setPatients] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [patientFilter, setPatientFilter] = useState('all');
  const [specialistFilter, setSpecialistFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [adminDialog, setAdminDialog] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchSchedule();
  }, [selectedDate, statusFilter, patientFilter, specialistFilter]);

  const fetchFilters = async () => {
    try {
      const [patientsRes, specialistsRes] = await Promise.all([
        axios.get(`${API_URL}/api/patients?limit=100`),
        axios.get(`${API_URL}/api/specialists?limit=100`)
      ]);
      setPatients(patientsRes.data);
      setSpecialists(specialistsRes.data);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/medications/schedule`, {
        params: {
          date: selectedDate,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          patient_id: patientFilter !== 'all' ? patientFilter : undefined,
          specialist_id: specialistFilter !== 'all' ? specialistFilter : undefined
        }
      });
      setSchedule(response.data);
    } catch (error) {
      console.error('Error fetching schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminister = async (recordId, administered, reason = null) => {
    try {
      await axios.post(`${API_URL}/api/medications/administer/${recordId}`, null, {
        params: {
          administered,
          reason_not_given: reason
        }
      });
      toast.success(administered ? 'Medicamento administrado' : 'Registro actualizado');
      setAdminDialog(null);
      fetchSchedule();
    } catch (error) {
      toast.error('Error registrando administración');
    }
  };

  const handleEditSchedule = (record) => {
    setEditingSchedule({
      id: record.id,
      scheduled_datetime: record.scheduled_datetime,
      notes: record.notes || '',
      status: record.status
    });
  };

  const handleUpdateSchedule = async () => {
    if (!editingSchedule) return;
    setEditLoading(true);
    try {
      await axios.put(`${API_URL}/api/medications/schedule/${editingSchedule.id}`, null, {
        params: {
          scheduled_datetime: editingSchedule.scheduled_datetime,
          notes: editingSchedule.notes,
          status: editingSchedule.status
        }
      });
      toast.success('Horario actualizado');
      setEditingSchedule(null);
      fetchSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando horario');
    }
    setEditLoading(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <Input 
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-auto"
          />
        </div>
        <Select value={patientFilter} onValueChange={setPatientFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrar paciente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los pacientes</SelectItem>
            {patients.map(p => (
              <SelectItem key={p.id} value={p.id}>
                {p.first_name} {p.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={specialistFilter} onValueChange={setSpecialistFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrar especialista" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los especialistas</SelectItem>
            {specialists.map(s => (
              <SelectItem key={s.id} value={s.id}>
                {s.first_name} {s.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filtrar estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="scheduled">Programados</SelectItem>
            <SelectItem value="administered">Administrados</SelectItem>
            <SelectItem value="missed">Omitidos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
        </div>
      ) : schedule.length > 0 ? (
        <div className="space-y-3">
          {schedule.map(record => (
            <Card key={record.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-center min-w-[60px]">
                      <p className="text-lg font-bold text-[#E63946]">
                        {new Date(record.scheduled_datetime).toLocaleTimeString('es-CO', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="h-12 w-12 rounded-lg bg-[#FDE8EA] flex items-center justify-center">
                      <Pill className="h-6 w-6 text-[#E63946]" />
                    </div>
                    <div>
                      <p className="font-medium">{record.medication_name}</p>
                      <p className="text-sm text-gray-500">
                        {record.dose} - {ROUTES[record.route] || record.route}
                      </p>
                      <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
                        <span>
                          <User className="h-3 w-3 inline mr-1" />
                          {record.patient_name || 'Sin paciente'}
                        </span>
                        {record.prescriber_name && (
                          <span className="text-blue-500">
                            | Dr. {record.prescriber_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={STATUS_COLORS[record.status]}>
                      {STATUS_LABELS[record.status]}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleEditSchedule(record)}
                      className="h-8 w-8 p-0"
                      data-testid={`edit-schedule-${record.id}`}
                    >
                      <Edit className="h-4 w-4 text-gray-500 hover:text-[#E63946]" />
                    </Button>
                    {record.status === 'scheduled' && (
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleAdminister(record.id, true)}
                          data-testid={`administer-btn-${record.id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Administrar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setAdminDialog(record)}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          No dado
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                {record.administered_at && (
                  <div className="mt-2 pt-2 border-t text-sm text-gray-500">
                    Administrado por {record.administrator_name} a las{' '}
                    {new Date(record.administered_at).toLocaleTimeString('es-CO', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No hay medicamentos programados para esta fecha</p>
        </div>
      )}

      {/* Not Given Dialog */}
      <Dialog open={!!adminDialog} onOpenChange={() => setAdminDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar medicamento no administrado</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Seleccione el motivo por el cual no se administró el medicamento:
            </p>
            <div className="space-y-2">
              {['Paciente ausente', 'Paciente rechazó', 'Orden médica de suspender', 'Efectos adversos', 'Otro'].map(reason => (
                <Button 
                  key={reason}
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => handleAdminister(adminDialog?.id, false, reason)}
                >
                  {reason}
                </Button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Schedule Dialog */}
      <Dialog open={!!editingSchedule} onOpenChange={() => setEditingSchedule(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Horario de Medicamento</DialogTitle>
          </DialogHeader>
          {editingSchedule && (
            <div className="space-y-4">
              <div>
                <Label>Fecha y Hora Programada</Label>
                <Input 
                  type="datetime-local"
                  value={editingSchedule.scheduled_datetime?.slice(0, 16) || ''}
                  onChange={(e) => setEditingSchedule({...editingSchedule, scheduled_datetime: e.target.value + ':00'})}
                  data-testid="edit-schedule-datetime"
                />
              </div>
              <div>
                <Label>Estado</Label>
                <Select 
                  value={editingSchedule.status}
                  onValueChange={(v) => setEditingSchedule({...editingSchedule, status: v})}
                >
                  <SelectTrigger data-testid="edit-schedule-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Programado</SelectItem>
                    <SelectItem value="administered">Administrado</SelectItem>
                    <SelectItem value="missed">Omitido</SelectItem>
                    <SelectItem value="held">Retenido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notas</Label>
                <Input 
                  value={editingSchedule.notes}
                  onChange={(e) => setEditingSchedule({...editingSchedule, notes: e.target.value})}
                  placeholder="Notas adicionales..."
                  data-testid="edit-schedule-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSchedule(null)} disabled={editLoading}>Cancelar</Button>
            <Button onClick={handleUpdateSchedule} disabled={editLoading} data-testid="update-schedule-btn">
              {editLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicationScheduleTab;
